import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Plus,
  Minus,
  ArrowLeft,
  Scissors,
  ArrowsOutCardinal,
  ArrowsClockwise,
  ArrowClockwise,
  ArrowCounterClockwise,
  Trash,
  MagicWand,
  Stack,
  FloppyDisk,
  ImageSquare,
  Check,
  X,
  UploadSimple,
  Crop,
  FlipHorizontal,
  Sparkle,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SearchSelect } from "@/components/SearchSelect";
import { InventorySlabPicker } from "@/components/InventorySlabPicker";
import { InventoryPhotoDialog } from "@/components/InventoryPhotoDialog";
import { NestingSettingsPopover } from "@/components/NestingSettingsPopover";
import { api, API, formatApiErrorDetail, photoHref } from "@/lib/api";
import { shrinkForUpload } from "@/lib/shrinkImage";
import { money } from "@/lib/format";

const DEFAULT_SLAB = { length_in: 126, width_in: 63 };
const SNAP = 0.5;
const uid = () => Math.random().toString(36).slice(2, 9);

const rotXY = ([x, y], r) => {
  if (!r) return [x, y];
  if (r === 90) return [-y, x];
  if (r === 180) return [-x, -y];
  if (r === 270) return [y, -x];
  const a = (r * Math.PI) / 180;
  const c = Math.cos(a);
  const s = Math.sin(a);
  return [x * c - y * s, x * s + y * c];
};

const r2 = (n) => Math.round(n * 100) / 100;

// keep on-slab text about the same size on screen whatever the slab is
const dimSize = (s) => Math.max(0.9, (Number(s?.length_in) || 126) / 115);

// exact inches, like the drawing: 96" / 25.5" / 70.13"
const inTag = (n) => `${Number(Number(n).toFixed(2))}"`;

// Rotate piece points in 90° steps and normalise them back to the origin
export const rotatePoints = (pts, rot) => {
  const r = (((Number(rot) || 0) % 360) + 360) % 360;
  const m = (pts || []).map((p) => rotXY(p, r));
  if (!m.length) return { pts: [], w: 0, h: 0 };
  const minX = Math.min(...m.map((p) => p[0]));
  const minY = Math.min(...m.map((p) => p[1]));
  const norm = m.map(([x, y]) => [r2(x - minX), r2(y - minY)]);
  return {
    pts: norm,
    w: Math.max(...norm.map((p) => p[0])),
    h: Math.max(...norm.map((p) => p[1])),
  };
};

// Cut-outs follow the same rotation so the drawing reads right on the slab
export const rotateCutout = (c, pts, rot) => {
  const r = (((Number(rot) || 0) % 360) + 360) % 360;
  const m = (pts || []).map((p) => rotXY(p, r));
  if (!m.length) return "";
  const minX = Math.min(...m.map((p) => p[0]));
  const minY = Math.min(...m.map((p) => p[1]));
  const x = Number(c.x) || 0;
  const y = Number(c.y) || 0;
  const w = Number(c.w) || 6;
  const h = Number(c.h) || 6;
  const cx = x + w / 2;
  const cy = y + h / 2;
  const cr = Number(c.rot) || 0;
  return [
    [x, y],
    [x + w, y],
    [x + w, y + h],
    [x, y + h],
  ]
    .map(([px, py]) => {
      const [dx, dy] = rotXY([px - cx, py - cy], cr);
      return [cx + dx, cy + dy];
    })
    .map((p) => rotXY(p, r))
    .map(([px, py]) => `${r2(px - minX)},${r2(py - minY)}`)
    .join(" ");
};

const polyArea = (pts) => {
  let a = 0;
  for (let i = 0; i < (pts || []).length; i++) {
    const [x1, y1] = pts[i];
    const [x2, y2] = pts[(i + 1) % pts.length];
    a += x1 * y2 - x2 * y1;
  }
  return Math.abs(a) / 2;
};

// Map a click on a rotated placement back to a split axis + position on the drawn piece
const sourceCut = (points, rot, localX, localY, axis, snap = 0) => {
  const r = (((Number(rot) || 0) % 360) + 360) % 360;
  const m = (points || []).map((p) => rotXY(p, r));
  if (!m.length) return { srcAxis: axis, at: 0 };
  const minX = Math.min(...m.map((p) => p[0]));
  const minY = Math.min(...m.map((p) => p[1]));
  const a = (r * Math.PI) / 180;
  const c = Math.cos(a);
  const s = Math.sin(a);
  const mx = localX + minX;
  const my = localY + minY;
  const sx = mx * c + my * s;
  const sy = -mx * s + my * c;
  const keepsAxis = Math.abs(c) >= Math.abs(s);
  const srcAxis = axis === 0 ? (keepsAxis ? 0 : 1) : keepsAxis ? 1 : 0;
  const step = snap > 0 ? snap : 1 / 16;
  return { srcAxis, at: Math.round((srcAxis === 0 ? sx : sy) / step) * step };
};

const materialKey = (color, thickness) =>
  `${(color || "").trim()}|${(thickness || "").trim()}`;

export const SlabLayoutBoard = ({
  areas = [],
  thickness = "",
  quoteColor = "",
  layout,
  onLayoutChange,
  materialOptions = [],
  onAreaMaterial,
  onAddArea,
  onSplitPiece,
  onMergeSeam,
  onUndo,
  onBeforeChange,
  undoCount = 0,
  rateFor,
  inventorySlabs = [],
  onBackToDrawing,
  onSave,
  saving = false,
  onVeinMatch,
  portalShown = true,
  onPortalShown,
  canvasHeight = "62vh",
  quoteId = "",
}) => {
  const [tool, setTool] = useState("move");
  const [cutDir, setCutDir] = useState("v");
  const [selCut, setSelCut] = useState(null);
  const [cutDrag, setCutDrag] = useState(null);
  const [seamDrawing, setSeamDrawing] = useState(true);
  const [cutSnap, setCutSnap] = useState(0); // 0 = no snap (1/16"), else inch step
  const [cutHover, setCutHover] = useState(null); // {slabIndex, pieceId, x, y}
  const [active, setActive] = useState(0);
  const [drag, setDrag] = useState(null);
  const [selected, setSelected] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [menu, setMenu] = useState(null);
  const [menuAngle, setMenuAngle] = useState(90);
  const photoRef = useRef(null);
  const sheetsRef = useRef(null);

  const slabs = layout?.slabs || [];
  const placements = layout?.placements || {};
  const cuts = layout?.cuts || [];

  const pieces = useMemo(
    () =>
      areas.flatMap((a) =>
        (a.pieces || []).map((p) => {
          const geo = rotatePoints(p.points, 0);
          return {
            ...p,
            area_id: a.id,
            area_name: a.name || "Area",
            color: a.slab_color || quoteColor,
            mat: materialKey(a.slab_color || quoteColor, thickness),
            sqft: Math.round((polyArea(p.points) / 144) * 100) / 100,
            base_w: geo.w,
            base_h: geo.h,
          };
        }),
      ),
    [areas, quoteColor, thickness],
  );

  const patch = (next) => {
    onBeforeChange?.();
    onLayoutChange({ ...(layout || {}), ...next });
  };

  // Ctrl+Z undo while the board is on screen (not while typing)
  useEffect(() => {
    if (!onUndo) return undefined;
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== "z" || e.shiftKey) return;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (["input", "textarea", "select"].includes(tag) || e.target?.isContentEditable) return;
      e.preventDefault();
      onUndo();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onUndo]);

  // mouse-wheel zoom over the sheets area (React onWheel is passive, so bind natively)
  useEffect(() => {
    const el = sheetsRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      const f = e.deltaY > 0 ? -0.15 : 0.15;
      setZoom((z) => Math.min(4, Math.max(0.2, Math.round((z + f) * 100) / 100)));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  // Fit every slab sheet inside the visible board height (no vertical scroll)
  const fitToHeight = () => {
    const el = sheetsRef.current;
    if (!el || !slabs.length) return;
    const cols = slabs.length > 1 && window.innerWidth >= 1280 ? 2 : 1;
    const rows = Math.ceil(slabs.length / cols);
    const availH = el.clientHeight;
    const availW = el.clientWidth;
    if (availH <= 0 || availW <= 0) return;
    const gap = 8;
    const header = 30;
    const cellW = (availW - (cols - 1) * gap) / cols;
    const perRowH = (availH - (rows - 1) * gap) / rows - header;
    const maxRatio = Math.max(
      ...slabs.map(
        (s) => (Number(s.width_in) || 1) / (Number(s.length_in) || 1),
      ),
    );
    if (perRowH <= 0 || cellW <= 0 || !isFinite(maxRatio)) return;
    const z = perRowH / (cellW * maxRatio);
    setZoom(Math.min(1, Math.max(0.2, Math.round(z * 100) / 100)));
  };

  // auto-fit on load, when the slab count changes, and on resize.
  // Run across a few frames so the fit lands after the board has its final size
  // (entering the Slab Layout tab mounts this fresh; layout/photos settle late).
  useEffect(() => {
    const raf = requestAnimationFrame(fitToHeight);
    const t1 = setTimeout(fitToHeight, 120);
    const t2 = setTimeout(fitToHeight, 320);
    window.addEventListener("resize", fitToHeight);
    let ro;
    if (typeof ResizeObserver !== "undefined" && sheetsRef.current) {
      ro = new ResizeObserver(fitToHeight);
      ro.observe(sheetsRef.current);
    }
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener("resize", fitToHeight);
      ro?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slabs.length, canvasHeight]);

  const addSlab = (mat) => {
    const [color] = (mat || "").split("|");
    const rec = inventorySlabs.find(
      (s) =>
        s.slab_color === color && (!thickness || s.thickness === thickness),
    );
    const next = [
      ...slabs,
      {
        id: uid(),
        material: color || quoteColor || "",
        thickness,
        slab_id: rec?.id || "",
        length_in: Number(rec?.length_in) || DEFAULT_SLAB.length_in,
        width_in: Number(rec?.width_in) || DEFAULT_SLAB.width_in,
      },
    ];
    patch({ slabs: next });
    setActive(next.length - 1);
    return next.length - 1;
  };

  // Add a real slab (or one of its child pieces) from inventory to the board,
  // using the piece's actual size + photo so nesting is true to the stone
  const addSlabFromInventory = (slab, piece) => {
    const invPhoto = (o) => (o?.photos || []).filter((p) => !p?.is_deleted)[0];
    const ph = invPhoto(piece) || invPhoto(slab);
    const next = [
      ...slabs,
      {
        id: uid(),
        material: slab.slab_color || slab.material || quoteColor || "",
        thickness: slab.thickness || thickness,
        slab_id: slab.id || "",
        piece_id: piece?.id || "",
        serial_number: piece?.serial_number || slab.serial_number || "",
        slab_code: slab.code || "",
        length_in:
          Number(piece?.length_in) ||
          Number(slab.length_in) ||
          DEFAULT_SLAB.length_in,
        width_in:
          Number(piece?.width_in) ||
          Number(slab.width_in) ||
          DEFAULT_SLAB.width_in,
        photo_url: ph?.storage_path || ph?.thumb_path || "",
      },
    ];
    patch({ slabs: next });
    setActive(next.length - 1);
    toast.success(
      piece
        ? `Added ${piece.serial_number || "slab"} from stock`
        : `Added ${slab.slab_color || "slab"} from stock`,
    );
    return next.length - 1;
  };

  const setSlab = (i, p) =>
    patch({ slabs: slabs.map((s, idx) => (idx === i ? { ...s, ...p } : s)) });

  const toggleMirror = (i) =>
    patch({
      slabs: slabs.map((s, idx) => (idx === i ? { ...s, mirror: !s.mirror } : s)),
    });

  // Bookmatch: drop a mirrored copy of the selected slab right beside it so
  // the veins meet symmetrically like an open book.
  const bookmatchSlab = () => {
    const i = active;
    const s = slabs[i];
    if (!s) {
      toast.error("Add or select a slab first");
      return;
    }
    const copy = { ...s, id: uid(), mirror: !s.mirror };
    const next = [...slabs];
    next.splice(i + 1, 0, copy);
    const nextPl = {};
    Object.entries(placements).forEach(([id, pl]) => {
      nextPl[id] = { ...pl, slab: pl.slab > i ? pl.slab + 1 : pl.slab };
    });
    const nextCuts = cuts.map((c) => ({
      ...c,
      slab: c.slab > i ? c.slab + 1 : c.slab,
    }));
    patch({ slabs: next, placements: nextPl, cuts: nextCuts });
    setActive(i + 1);
    toast.success("Bookmatched slab added — veins mirrored");
  };

  // slab photo: uploaded once, then cropped to the sheet with zoom + offset
  const photoBox = (s) => {
    const zoom = Math.max(0.1, Number(s.photo_zoom) || 1);
    return {
      x: Number(s.photo_x) || 0,
      y: Number(s.photo_y) || 0,
      width: (Number(s.length_in) || 1) * zoom,
      height: (Number(s.width_in) || 1) * zoom,
    };
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !slabs[active]) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", await shrinkForUpload(file));
    try {
      const { data } = await api.post("/quotes/layout-photo", fd);
      setSlab(active, {
        photo_url: `${API}/photos/${data.storage_path}?auth=${localStorage.getItem("sw_token")}`,
        photo_zoom: 1,
        photo_x: 0,
        photo_y: 0,
      });
      toast.success("Slab photo added");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not upload that photo",
      );
    }
    setUploading(false);
  };

  const removeSlab = (i) => {
    const nextPl = {};
    Object.entries(placements).forEach(([id, pl]) => {
      if (pl.slab === i) return;
      nextPl[id] = { ...pl, slab: pl.slab > i ? pl.slab - 1 : pl.slab };
    });
    patch({
      slabs: slabs.filter((_, idx) => idx !== i),
      placements: nextPl,
      cuts: cuts
        .filter((c) => c.slab !== i)
        .map((c) => ({ ...c, slab: c.slab > i ? c.slab - 1 : c.slab })),
    });
    setActive(0);
  };

  const place = (piece, slabIndex, x = 1, y = 1, rot = 0) =>
    patch({
      placements: {
        ...placements,
        [piece.id]: { slab: slabIndex, x, y, rot },
      },
    });

  const unplace = (pieceId) => {
    const next = { ...placements };
    delete next[pieceId];
    patch({ placements: next });
  };

  const rotate = (pieceId, deg = 90) => {
    const pl = placements[pieceId];
    if (!pl) return;
    patch({
      placements: {
        ...placements,
        [pieceId]: {
          ...pl,
          rot: ((((pl.rot || 0) + deg) % 360) + 360) % 360,
        },
      },
    });
  };

  const moveToSlab = (pieceId, slabIndex) => {
    const pl = placements[pieceId];
    if (!pl) return;
    patch({
      placements: {
        ...placements,
        [pieceId]: { ...pl, slab: slabIndex, x: 1, y: 1 },
      },
    });
    setActive(slabIndex);
    setMenu(null);
  };

  const autoNest = () => {
    const nextSlabs = [...slabs];
    const nextPl = {};
    const groups = {};
    pieces.forEach((p) => {
      groups[p.mat] = groups[p.mat] || [];
      groups[p.mat].push(p);
    });
    Object.entries(groups).forEach(([mat, list]) => {
      const [color] = mat.split("|");
      const sorted = [...list].sort(
        (a, b) => b.base_w * b.base_h - a.base_w * a.base_h,
      );
      const rec = inventorySlabs.find((s) => s.slab_color === color);
      const sheetFor = (idx) => nextSlabs[idx];
      let idx = nextSlabs.findIndex(
        (s) => materialKey(s.material, s.thickness) === mat,
      );
      if (idx < 0) {
        nextSlabs.push({
          id: uid(),
          material: color || quoteColor || "",
          thickness,
          slab_id: rec?.id || "",
          length_in: Number(rec?.length_in) || DEFAULT_SLAB.length_in,
          width_in: Number(rec?.width_in) || DEFAULT_SLAB.width_in,
        });
        idx = nextSlabs.length - 1;
      }
      let cursor = { x: 1, y: 1, rowH: 0 };
      sorted.forEach((p) => {
        let placed = false;
        let guard = 0;
        while (!placed && guard++ < 40) {
          const sheet = sheetFor(idx);
          const L = sheet.length_in - 1;
          const W = sheet.width_in - 1;
          const opts = [0, 90]
            .map((rot) => ({ rot, ...rotatePoints(p.points, rot) }))
            .filter((o) => o.w <= L - 1 && o.h <= W - 1);
          if (!opts.length) break; // bigger than a slab — leave it off the layout
          const fit =
            opts.find((o) => cursor.x + o.w <= L && cursor.y + o.h <= W) ||
            null;
          if (fit) {
            nextPl[p.id] = {
              slab: idx,
              x: Math.round(cursor.x * 2) / 2,
              y: Math.round(cursor.y * 2) / 2,
              rot: fit.rot,
            };
            cursor.x += fit.w + 1;
            cursor.rowH = Math.max(cursor.rowH, fit.h);
            placed = true;
            continue;
          }
          const newRow = opts.find(
            (o) => cursor.y + cursor.rowH + 1 + o.h <= W && o.w <= L - 1,
          );
          if (cursor.rowH && newRow) {
            cursor = { x: 1, y: cursor.y + cursor.rowH + 1, rowH: 0 };
            continue;
          }
          const nextIdx = nextSlabs.findIndex(
            (s, i) => i > idx && materialKey(s.material, s.thickness) === mat,
          );
          if (nextIdx >= 0) {
            idx = nextIdx;
          } else {
            nextSlabs.push({
              id: uid(),
              material: color || quoteColor || "",
              thickness,
              slab_id: rec?.id || "",
              length_in: Number(rec?.length_in) || DEFAULT_SLAB.length_in,
              width_in: Number(rec?.width_in) || DEFAULT_SLAB.width_in,
            });
            idx = nextSlabs.length - 1;
          }
          cursor = { x: 1, y: 1, rowH: 0 };
        }
      });
    });
    patch({ slabs: nextSlabs, placements: nextPl });
    setActive(0);
  };

  const svgPoint = (e, slabIndex) => {
    const svg = e.currentTarget.closest("svg");
    const rect = svg.getBoundingClientRect();
    const s = slabs[slabIndex];
    const pxPerIn = rect.width / s.length_in;
    return {
      x: (e.clientX - rect.left) / pxPerIn,
      y: (e.clientY - rect.top) / pxPerIn,
      rect,
      pxPerIn,
    };
  };

  const onPieceDown = (e, piece, slabIndex) => {
    e.stopPropagation();
    setMenu(null);
    setSelected(piece.id);
    setActive(slabIndex);
    const pl = placements[piece.id];
    const pt = svgPoint(e, slabIndex);
    if (tool === "cut") {
      const axis = cutDir === "v" ? 0 : 1;
      // map the click back to the unrotated piece so the split lands where clicked
      const { srcAxis, at } = sourceCut(
        piece.points,
        pl.rot || 0,
        pt.x - pl.x,
        pt.y - pl.y,
        axis,
        cutSnap,
      );
      if (seamDrawing) {
        onSplitPiece?.(piece.area_id, piece.id, srcAxis, at);
      } else {
        patch({
          cuts: [
            ...cuts,
            {
              id: uid(),
              slab: slabIndex,
              dir: cutDir,
              at: Math.round((cutDir === "v" ? pt.x : pt.y) * 2) / 2,
            },
          ],
        });
      }
      return;
    }
    setDrag({
      pieceId: piece.id,
      slabIndex,
      dx: pt.x - pl.x,
      dy: pt.y - pl.y,
    });
  };

  const openMenu = (e, piece, slabIndex) => {
    e.preventDefault();
    e.stopPropagation();
    const pt = svgPoint(e, slabIndex);
    const host = e.currentTarget.closest("[data-layout-sheet]");
    const box = host?.getBoundingClientRect();
    setSelected(piece.id);
    setActive(slabIndex);
    const rawLeft = box ? e.clientX - box.left : 0;
    setMenu({
      pieceId: piece.id,
      slabIndex,
      at: pt,
      left: box
        ? Math.min(Math.max(rawLeft, 170), Math.max(box.width - 170, 170))
        : rawLeft,
      top: box ? Math.max(e.clientY - box.top, 8) : 0,
    });
  };

  const seamFromMenu = (dir) => {
    if (!menu) return;
    const piece = pieces.find((p) => p.id === menu.pieceId);
    const pl = placements[menu.pieceId];
    if (!piece || !pl) return;
    const { srcAxis, at } = sourceCut(
      piece.points,
      pl.rot || 0,
      menu.at.x - pl.x,
      menu.at.y - pl.y,
      dir === "v" ? 0 : 1,
      cutSnap,
    );
    onSplitPiece?.(piece.area_id, piece.id, srcAxis, at);
    setMenu(null);
  };

  const onMove = (e, slabIndex) => {
    if (cutDrag) {
      const s = slabs[slabIndex];
      const c = cuts.find((x) => x.id === cutDrag.id);
      if (!c) return;
      const pt = svgPoint(e, slabIndex);
      const max = c.dir === "v" ? s.length_in : s.width_in;
      const at = Math.min(
        Math.max(Math.round((c.dir === "v" ? pt.x : pt.y) / SNAP) * SNAP, 0),
        max,
      );
      patch({ cuts: cuts.map((x) => (x.id === c.id ? { ...x, at } : x)) });
      return;
    }
    if (tool === "cut" && !drag) {
      const pt = svgPoint(e, slabIndex);
      const step = cutSnap > 0 ? cutSnap : 1 / 16;
      setCutHover({ slabIndex, x: Math.round(pt.x / step) * step, y: Math.round(pt.y / step) * step });
      return;
    }
    if (!drag) return;
    const s = slabs[slabIndex];
    const pt = svgPoint(e, slabIndex);
    const pl = placements[drag.pieceId];
    const geo = rotatePoints(
      pieces.find((p) => p.id === drag.pieceId)?.points || [],
      pl.rot || 0,
    );
    // pieces may hang past the slab edge (to cut a seam) — keep at least 2" on the slab
    const x = Math.min(
      Math.max(Math.round((pt.x - drag.dx) / SNAP) * SNAP, Math.min(0, 2 - geo.w)),
      Math.max(s.length_in - 2, 0),
    );
    const y = Math.min(
      Math.max(Math.round((pt.y - drag.dy) / SNAP) * SNAP, Math.min(0, 2 - geo.h)),
      Math.max(s.width_in - 2, 0),
    );
    patch({
      placements: {
        ...placements,
        [drag.pieceId]: { ...pl, slab: slabIndex, x, y },
      },
    });
  };

  // seam numbers: every seamed edge gets a badge; edges cut from the same seam share a number
  const seamNumbers = (() => {
    const map = {};
    let n = 0;
    const groups = {};
    pieces.forEach((p) =>
      (p.edges || []).forEach((e, k) => {
        if (!e?.seam || p.panel_kind) return;
        const key = e.seam_id || e.mate || `${p.id}:${k}`;
        if (!groups[key]) groups[key] = ++n;
        map[`${p.id}:${k}`] = groups[key];
      }),
    );
    return map;
  })();

  const slabStats = (i) => {
    const s = slabs[i];
    const on = pieces.filter((p) => placements[p.id]?.slab === i);
    const used = on.reduce((n, p) => n + p.sqft, 0);
    const total = (s.length_in * s.width_in) / 144;
    return {
      used: Math.round(used * 100) / 100,
      total: Math.round(total * 100) / 100,
      pct: total ? Math.round((used / total) * 100) : 0,
      count: on.length,
    };
  };

  const materialPrice = slabs.reduce((sum, s) => {
    const rate = rateFor?.(s.material) || 0;
    return sum + ((s.length_in * s.width_in) / 144) * rate;
  }, 0);

  const areaStats = areas.map((a) => {
    const list = pieces.filter((p) => p.area_id === a.id);
    const matched = list.filter((p) => placements[p.id]);
    const total = Math.round(list.reduce((n, p) => n + p.sqft, 0) * 100) / 100;
    const done =
      Math.round(matched.reduce((n, p) => n + p.sqft, 0) * 100) / 100;
    return {
      area: a,
      total,
      done,
      pct: total ? Math.round((done / total) * 100) : 0,
    };
  });

  const overallPct = (() => {
    const t = areaStats.reduce((n, s) => n + s.total, 0);
    const d = areaStats.reduce((n, s) => n + s.done, 0);
    return t ? Math.round((d / t) * 100) : 0;
  })();

  const activeSlab = slabs[active];
  const [invPick, setInvPick] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [veinMatch, setVeinMatch] = useState(true);
  const [aiNotes, setAiNotes] = useState("");
  const [aiStats, setAiStats] = useState(null);
  const [aiJoints, setAiJoints] = useState([]);

  const aiLayout = async () => {
    if (!pieces.length) {
      toast.error("Draw some pieces first");
      return;
    }
    setAiBusy(true);
    try {
      const templates = {};
      pieces.forEach((p) => {
        if (templates[p.mat]) return;
        const [color] = p.mat.split("|");
        const rec = inventorySlabs.find(
          (s) => s.slab_color === color && (!thickness || s.thickness === thickness),
        );
        templates[p.mat] = {
          material: color || quoteColor,
          thickness,
          slab_id: rec?.id || "",
          length_in: Number(rec?.length_in) || DEFAULT_SLAB.length_in,
          width_in: Number(rec?.width_in) || DEFAULT_SLAB.width_in,
        };
      });
      const { data } = await api.post("/quotes/ai-layout", {
        pieces: pieces.map((p) => ({
          id: p.id,
          label: p.label || "",
          area_name: p.area_name,
          mat: p.mat,
          kind: p.orientation === "vertical" ? "backsplash" : "countertop",
          points: p.points,
          x: Number(p.x) || 0,
          y: Number(p.y) || 0,
          rot: Number(p.rot) || 0,
          edges: p.edges || [],
        })),
        slabs,
        templates,
        vein_match: veinMatch,
        quote_color: quoteColor,
        thickness,
      });
      patch({ slabs: data.slabs, placements: data.placements, cuts: [], remnants: data.remnants || [] });
      setActive(0);
      setAiStats(data.stats);
      setAiJoints(data.joints || []);
      setAiNotes(data.notes || "");
      const n = data.stats.slabs_needed;
      const veinsRead = Object.keys(data.veins || {}).length;
      toast.success(
        `${n} slab${n === 1 ? "" : "s"} needed · ${data.stats.yield_pct}% yield` +
          (data.bookmatched?.length ? ` · ${data.bookmatched.length} bookmatch pair(s)` : "") +
          (veinsRead ? ` · veins read from ${veinsRead} photo(s)` : "") +
          (data.unplaced.length ? ` · ${data.unplaced.length} piece(s) too big for a slab` : ""),
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "AI layout failed");
    }
    setAiBusy(false);
  };

  // Read vein direction off the real slab photos and remember it on each slab
  const readVeins = async () => {
    const withPhoto = slabs.filter((s) => s.photo_url);
    if (!withPhoto.length) {
      toast.error("Add a slab photo first (upload or pick from stock)");
      return;
    }
    setAiBusy(true);
    try {
      const { data } = await api.post("/quotes/ai-layout/veins", { slabs: withPhoto });
      const read = data.veins || {};
      patch({ slabs: slabs.map((s) => (read[s.id] ? { ...s, ...read[s.id] } : s)) });
      const n = Object.keys(read).length;
      toast.success(n ? `Veins read on ${n} slab${n === 1 ? "" : "s"}` : "Could not read veins from those photos");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Vein read failed");
    }
    setAiBusy(false);
  };

  const billSlabs = !!layout?.bill_slabs;
  const invSlabCount = slabs.filter((s) => s.slab_id).length;
  const remnantRects = layout?.remnants || [];

  // One tap: every inventory slab on the board goes on hold for this quote
  const holdLayoutSlabs = async () => {
    if (!quoteId) {
      toast.error("Save the quote first, then hold slabs");
      return;
    }
    if (onSave) await onSave();
    setAiBusy(true);
    try {
      const { data } = await api.post(`/quotes/${quoteId}/stock/hold-layout`);
      const n = data.added.length;
      if (n) toast.success(`${n} slab${n === 1 ? "" : "s"} on hold for this quote`);
      else if (data.skipped.length) toast.error(data.skipped.join(" · "));
      else toast.message(data.no_inventory ? "Pick real slabs from stock first — blank slabs can't be held" : "Those slabs are already held");
      if (data.skipped.length && n) toast.message(`Skipped: ${data.skipped.join(" · ")}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not hold slabs");
    }
    setAiBusy(false);
  };

  // Turn the AI layout's off-cuts into remnant records (once the job is cut)
  const captureRemnants = async () => {
    if (!quoteId) {
      toast.error("Save the quote first");
      return;
    }
    if (onSave) await onSave();
    setAiBusy(true);
    try {
      const { data } = await api.post(`/quotes/${quoteId}/remnants-from-layout`);
      const n = data.created.length;
      toast.success(
        n ? `${n} remnant${n === 1 ? "" : "s"} added to inventory (${data.created.map((r) => r.code).join(", ")})`
          : "Those remnants are already in inventory",
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save remnants");
    }
    setAiBusy(false);
  };
  const unplaced = pieces.filter((p) => !placements[p.id]);

  return (
    <div className="flex flex-col xl:flex-row" data-testid="slab-layout-board">
      <aside className="shrink-0 border-b border-zinc-800 bg-zinc-900/60 p-3 xl:w-64 xl:border-b-0 xl:border-r">
        <div className="flex items-center gap-3">
          <div
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2 border-indigo-400/60 text-xs font-bold text-indigo-200"
            data-testid="layout-matched-pct"
          >
            {overallPct}%
          </div>
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
              Packages ({areas.length})
            </div>
            <div className="font-mono-data text-[11px] text-zinc-400">
              {areaStats.reduce((n, s) => n + s.done, 0).toFixed(2)} /{" "}
              {areaStats.reduce((n, s) => n + s.total, 0).toFixed(2)} ft²
            </div>
          </div>
        </div>

        <div className="mt-3 space-y-2" data-testid="layout-packages">
          {areaStats.map(({ area, total, done, pct }) => (
            <div
              key={area.id}
              data-testid={`layout-package-${area.id}`}
              className="rounded-lg border border-zinc-800 bg-zinc-950 p-2.5"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-xs font-bold">{area.name}</span>
                <span className="font-mono-data text-[10px] text-zinc-500">
                  {pct}%
                </span>
              </div>
              <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-zinc-800">
                <div
                  className="h-full bg-indigo-400 transition-all duration-300"
                  style={{ width: `${Math.min(pct, 100)}%` }}
                />
              </div>
              <div className="font-mono-data mt-1 text-[10px] text-zinc-500">
                {done.toFixed(2)} / {total.toFixed(2)} ft²
              </div>
              <div className="mt-2">
                <SearchSelect
                  testId={`layout-material-${area.id}`}
                  value={area.slab_color || ""}
                  options={materialOptions}
                  allowCustom
                  placeholder={quoteColor || "Assign material"}
                  searchPlaceholder="Slabs, remnants or a color…"
                  onChange={(v) => onAreaMaterial?.(area.id, v)}
                  className="h-9 px-2 text-[11px]"
                />
              </div>
            </div>
          ))}
          <Button
            data-testid="layout-add-package"
            variant="outline"
            onClick={onAddArea}
            className="h-10 w-full border-dashed border-zinc-700 bg-transparent text-[10px] uppercase tracking-widest hover:bg-zinc-800"
          >
            <Plus size={13} weight="bold" className="mr-1" /> Add package
          </Button>
        </div>

        {unplaced.length > 0 && (
          <div className="mt-4" data-testid="layout-unplaced">
            <div className="text-[10px] font-bold uppercase tracking-widest text-amber-300">
              Not on a slab ({unplaced.length})
            </div>
            <div className="mt-2 space-y-1">
              {unplaced.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  data-testid={`layout-place-${p.id}`}
                  onClick={() => {
                    const idx = slabs.length ? active : addSlab(p.mat);
                    place(p, idx);
                  }}
                  className="flex w-full items-center justify-between gap-2 rounded-md border border-zinc-800 bg-zinc-950 px-2 py-2 text-left text-[11px] transition-colors duration-150 hover:border-indigo-400/50 hover:bg-zinc-800"
                >
                  <span className="truncate">
                    {p.area_name} · {p.label}
                  </span>
                  <span className="font-mono-data shrink-0 text-[10px] text-zinc-500">
                    {p.sqft} ft²
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </aside>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2 border-b border-zinc-800 bg-zinc-900/40 px-3 py-2">
          {onBackToDrawing && (
            <button
              type="button"
              data-testid="layout-back-to-drawing-top"
              onClick={onBackToDrawing}
              className="flex h-9 items-center gap-1.5 rounded-full border border-zinc-700 px-3 text-[10px] font-bold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:bg-zinc-800 hover:text-white"
            >
              <ArrowLeft size={13} weight="bold" /> Drawing
            </button>
          )}
          <SearchSelect
            testId="layout-add-slab"
            value=""
            options={[...new Set(pieces.map((p) => p.mat))]
              .filter(Boolean)
              .map((m) => ({
                value: m,
                label:
                  `${m.split("|")[0] || "Slab"} ${m.split("|")[1] || ""}`.trim(),
                hint: "Add",
              }))}
            placeholder="+ Add a slab"
            searchPlaceholder="Material…"
            emptyText="Assign a material to a package first"
            onChange={(m) => addSlab(m)}
            className="h-9 w-44 px-3 text-xs"
          />
          <InventorySlabPicker
            testId="layout-inv-slab-picker"
            quoteColor={quoteColor}
            thickness={thickness}
            onPick={addSlabFromInventory}
          />
          <button
            type="button"
            data-testid="layout-add-blank-slab"
            onClick={() => addSlab(materialKey(quoteColor, thickness))}
            title="Add another slab of this material"
            className="flex h-9 items-center gap-1.5 rounded-lg border border-zinc-700 px-3 text-[10px] font-bold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:border-indigo-400/50 hover:bg-zinc-800 hover:text-white"
          >
            <Plus size={13} weight="bold" /> Slab
          </button>
          <button
            type="button"
            data-testid="layout-bookmatch"
            onClick={bookmatchSlab}
            disabled={!slabs.length}
            title="Add a mirrored (bookmatched) copy of the selected slab"
            className="flex h-9 items-center gap-1.5 rounded-lg border border-cyan-400/40 bg-cyan-500/10 px-3 text-[10px] font-bold uppercase tracking-widest text-cyan-200 transition-colors duration-150 hover:bg-cyan-500/20 disabled:opacity-40"
          >
            <FlipHorizontal size={13} weight="bold" /> Bookmatch
          </button>
          {[
            ["move", ArrowsOutCardinal, "Move pieces"],
            ["cut", Scissors, "Cut a seam"],
          ].map(([key, Icon, label]) => (
            <button
              key={key}
              type="button"
              data-testid={`layout-tool-${key}`}
              title={label}
              onClick={() => setTool(key)}
              className={`flex h-9 w-9 items-center justify-center rounded-lg border transition-all duration-150 ${
                tool === key
                  ? "border-indigo-400/60 bg-indigo-500 text-white"
                  : "border-zinc-700 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
              }`}
            >
              <Icon size={15} weight="bold" />
            </button>
          ))}
          {cuts.length > 0 && (
            <button
              type="button"
              data-testid="layout-remove-all-seams"
              onClick={() => {
                patch({ cuts: [] });
                setSelCut(null);
                toast.success("All seams removed");
              }}
              title="Clear every seam on the layout"
              className="flex h-9 items-center gap-1.5 rounded-lg border border-red-500/40 bg-red-500/10 px-3 text-[10px] font-bold uppercase tracking-widest text-red-300 transition-colors duration-150 hover:bg-red-500/20"
            >
              <Scissors size={13} weight="bold" /> Remove All Seams
            </button>
          )}
          {tool === "cut" && (
            <>
              <button
                type="button"
                data-testid="layout-cut-dir"
                onClick={() => setCutDir((d) => (d === "v" ? "h" : "v"))}
                className="h-9 rounded-lg border border-zinc-700 px-2.5 text-[10px] font-bold uppercase tracking-widest text-zinc-300 hover:bg-zinc-800"
              >
                {cutDir === "v" ? "Vertical cut" : "Horizontal cut"}
              </button>
              <button
                type="button"
                data-testid="layout-cut-seam-toggle"
                onClick={() => setSeamDrawing((v) => !v)}
                className={`flex h-9 items-center gap-1 rounded-lg border px-2.5 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                  seamDrawing
                    ? "border-indigo-400/50 bg-indigo-500/10 text-indigo-200"
                    : "border-zinc-700 text-zinc-400"
                }`}
              >
                {seamDrawing ? (
                  <Check size={12} weight="bold" />
                ) : (
                  <X size={12} weight="bold" />
                )}
                Seam the drawing
              </button>
              <div
                className="flex h-9 items-center gap-1 rounded-lg border border-zinc-700 px-2"
                data-testid="layout-cut-snap"
              >
                <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">Snap</span>
                <input
                  type="number"
                  min="0"
                  step="0.125"
                  value={cutSnap}
                  onChange={(e) => setCutSnap(Math.max(0, Number(e.target.value) || 0))}
                  data-testid="layout-cut-snap-input"
                  className="h-7 w-14 rounded border border-zinc-700 bg-zinc-950 px-1.5 text-center font-mono-data text-xs text-zinc-100"
                />
                <span className="text-[10px] text-zinc-500">in</span>
                {[
                  [0, "No snap"],
                  [0.125, "1/8"],
                  [0.25, "1/4"],
                  [0.5, "1/2"],
                  [1, "1"],
                  [6, "6"],
                  [12, "12"],
                ].map(([v, label]) => (
                  <button
                    key={v}
                    type="button"
                    data-testid={`layout-cut-snap-${String(v).replace(".", "_")}`}
                    onClick={() => setCutSnap(v)}
                    className={`rounded px-1.5 py-1 text-[10px] font-bold transition-colors duration-150 ${
                      cutSnap === v
                        ? "bg-indigo-500 text-white"
                        : "text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </>
          )}
          <Button
            data-testid="layout-auto-nest"
            onClick={autoNest}
            className="h-9 bg-indigo-500 text-[10px] font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
          >
            <MagicWand size={13} weight="bold" className="mr-1.5" /> Auto nest
          </Button>
          <div className="flex h-9 items-stretch overflow-hidden rounded-lg border border-amber-400/50">
            <button
              type="button"
              data-testid="layout-ai-layout"
              disabled={aiBusy}
              onClick={aiLayout}
              title="Nest every piece, count slabs and check vein continuity across seams"
              className="flex items-center gap-1.5 bg-amber-500/15 px-3 text-[10px] font-bold uppercase tracking-widest text-amber-100 transition-colors duration-150 hover:bg-amber-500/30 disabled:opacity-50"
            >
              <Sparkle size={13} weight="bold" className={aiBusy ? "animate-pulse" : ""} />
              {aiBusy ? "Laying out…" : "AI layout"}
            </button>
            <button
              type="button"
              data-testid="layout-ai-vein-toggle"
              onClick={() => setVeinMatch((v) => !v)}
              title="Keep seamed pieces together on one slab, same orientation, so veins run across the joint"
              className={`flex items-center gap-1 border-l border-amber-400/40 px-2.5 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                veinMatch ? "bg-amber-400 text-zinc-950" : "text-amber-200/70 hover:bg-amber-500/10"
              }`}
            >
              {veinMatch ? <Check size={11} weight="bold" /> : <X size={11} weight="bold" />}
              Vein match
            </button>
            <button
              type="button"
              data-testid="layout-ai-read-veins"
              disabled={aiBusy || !slabs.some((s) => s.photo_url)}
              onClick={readVeins}
              title="Read vein direction and flow from the slab photos on the board"
              className="flex items-center gap-1 border-l border-amber-400/40 px-2.5 text-[10px] font-bold uppercase tracking-widest text-amber-200/80 transition-colors duration-150 hover:bg-amber-500/10 disabled:opacity-40"
            >
              <ImageSquare size={11} weight="bold" /> Read veins
            </button>
          </div>
          <NestingSettingsPopover />
          {slabs.length > 0 && (
            <button
              type="button"
              data-testid="layout-bill-slabs"
              onClick={() => {
                patch({ bill_slabs: !billSlabs });
                toast.success(
                  !billSlabs
                    ? `Quote now bills ${slabs.length} full slab${slabs.length === 1 ? "" : "s"} from this layout`
                    : "Quote bills material by square foot again",
                );
              }}
              title="Bill the customer for the whole slabs in this layout instead of square feet"
              className={`flex h-9 items-center gap-1.5 rounded-lg border px-3 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                billSlabs
                  ? "border-emerald-500/60 bg-emerald-500/15 text-emerald-200"
                  : "border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              }`}
            >
              <Stack size={13} weight="bold" />
              {billSlabs ? `Billing ${slabs.length} slab${slabs.length === 1 ? "" : "s"}` : "Bill by slab"}
            </button>
          )}
          {invSlabCount > 0 && (
            <button
              type="button"
              data-testid="layout-hold-slabs"
              disabled={aiBusy}
              onClick={holdLayoutSlabs}
              title="Put every inventory slab on this board on hold for this quote"
              className="flex h-9 items-center gap-1.5 rounded-lg border border-yellow-500/50 bg-yellow-500/10 px-3 text-[10px] font-bold uppercase tracking-widest text-yellow-200 transition-colors duration-150 hover:bg-yellow-500/20 disabled:opacity-40"
            >
              <Stack size={13} weight="bold" /> Hold {invSlabCount} slab{invSlabCount === 1 ? "" : "s"}
            </button>
          )}
          {remnantRects.length > 0 && (
            <button
              type="button"
              data-testid="layout-capture-remnants"
              disabled={aiBusy}
              onClick={captureRemnants}
              title="Add the usable off-cuts from this layout to remnant inventory"
              className="flex h-9 items-center gap-1.5 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 text-[10px] font-bold uppercase tracking-widest text-emerald-200 transition-colors duration-150 hover:bg-emerald-500/20 disabled:opacity-40"
            >
              <Scissors size={13} weight="bold" /> Save {remnantRects.length} remnant{remnantRects.length === 1 ? "" : "s"}
            </button>
          )}
          {onSave && (
            <Button
              data-testid="layout-save-button"
              variant="outline"
              disabled={saving}
              onClick={onSave}
              className="h-9 border-zinc-700 bg-transparent text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-800"
            >
              <FloppyDisk size={13} weight="bold" className="mr-1.5" />
              {saving ? "Saving…" : "Save"}
            </Button>
          )}
          {onVeinMatch && (
            <button
              type="button"
              data-testid="layout-open-vein-match"
              onClick={onVeinMatch}
              className="flex h-9 items-center gap-1.5 rounded-full border border-cyan-400/40 bg-cyan-500/10 px-3 text-[10px] font-bold uppercase tracking-widest text-cyan-200 transition-colors duration-150 hover:bg-cyan-500/20"
            >
              <ImageSquare size={13} weight="bold" /> Vein match
            </button>
          )}
          {onUndo && (
            <button
              type="button"
              data-testid="layout-undo"
              onClick={onUndo}
              disabled={!undoCount}
              title="Undo the last layout change (Ctrl+Z)"
              className="ml-auto inline-flex h-8 items-center gap-1.5 rounded-lg border border-zinc-700 bg-zinc-950 px-2.5 text-[10px] font-bold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:bg-zinc-800 disabled:opacity-40"
            >
              <ArrowCounterClockwise size={12} weight="bold" /> Undo{undoCount ? ` (${undoCount})` : ""}
            </button>
          )}
          <div
            className={`font-mono-data flex items-center gap-1 rounded-lg border border-zinc-800 bg-zinc-950 px-1.5 py-1 ${onUndo ? "" : "ml-auto"}`}
            data-testid="layout-zoom-bar"
          >
            <button
              type="button"
              data-testid="layout-zoom-out"
              onClick={() =>
                setZoom((z) =>
                  Math.max(0.2, Math.round((z - 0.25) * 100) / 100),
                )
              }
              className="rounded-md border border-zinc-700 p-1 text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
            >
              <Minus size={12} weight="bold" />
            </button>
            <span
              className="w-10 text-center text-[10px] text-zinc-300"
              data-testid="layout-zoom-value"
            >
              {Math.round(zoom * 100)}%
            </span>
            <button
              type="button"
              data-testid="layout-zoom-in"
              onClick={() =>
                setZoom((z) => Math.min(4, Math.round((z + 0.25) * 100) / 100))
              }
              className="rounded-md border border-zinc-700 p-1 text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
            >
              <Plus size={12} weight="bold" />
            </button>
            <button
              type="button"
              data-testid="layout-zoom-fit"
              onClick={fitToHeight}
              title="Fit the slabs to the screen height"
              className="rounded-md border border-zinc-700 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
            >
              Fit
            </button>
          </div>
          <div
            className="font-mono-data rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-1.5 text-[11px] text-zinc-400"
            data-testid="layout-price-readout"
          >
            Material price: {money(materialPrice)} ·{" "}
            <span className="text-indigo-300">{slabs.length} slabs</span>
          </div>
        </div>

        {aiNotes && (
          <div
            data-testid="layout-ai-notes"
            className="mb-2 rounded-lg border border-amber-400/30 bg-amber-500/5 px-4 py-3 text-sm text-zinc-200"
          >
            <div className="mb-1 flex items-center justify-between gap-3">
              <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-amber-200">
                <Sparkle size={12} weight="bold" /> AI layout check
                {aiStats && (
                  <span className="font-mono-data normal-case tracking-normal text-zinc-400">
                    · {aiStats.slabs_needed} slab{aiStats.slabs_needed === 1 ? "" : "s"} · {aiStats.used_sqft} of {aiStats.slab_sqft} SF · {aiStats.yield_pct}% yield
                  </span>
                )}
              </span>
              <button
                type="button"
                data-testid="layout-ai-notes-close"
                onClick={() => setAiNotes("")}
                className="text-zinc-500 hover:text-zinc-200"
              >
                <X size={14} weight="bold" />
              </button>
            </div>
            <div className="whitespace-pre-wrap leading-relaxed">{aiNotes}</div>
            {aiJoints.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="layout-ai-joints">
                {aiJoints.map((j, i) => (
                  <span
                    key={i}
                    title={j.why}
                    className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${
                      j.ok
                        ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
                        : "border-red-500/40 bg-red-500/10 text-red-200"
                    }`}
                  >
                    {(j.labels || j.pieces).join(" + ")}: {j.ok ? "veins match" : "check seam"}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        <div
          ref={sheetsRef}
          className={`gap-6 overflow-auto p-4 ${
            slabs.length > 1 ? "grid xl:grid-cols-2" : "grid grid-cols-1"
          }`}
          style={{ height: canvasHeight }}
          data-testid="layout-sheets"
        >
          {slabs.length === 0 && (
            <div
              className="rounded-lg border border-dashed border-zinc-800 p-10 text-center"
              data-testid="layout-empty"
            >
              <Stack
                size={24}
                weight="bold"
                className="mx-auto text-zinc-700"
              />
              <p className="mt-3 text-sm text-zinc-400">
                Add a slab, or hit Auto nest and the pieces will lay themselves
                out.
              </p>
            </div>
          )}
          {slabs.map((s, i) => {
            const st = slabStats(i);
            const on = pieces.filter((p) => placements[p.id]?.slab === i);
            return (
              <div key={s.id || i} data-testid={`layout-slab-${i}`}>
                <div className="flex flex-wrap items-center gap-2 pb-1">
                  <button
                    type="button"
                    data-testid={`layout-slab-select-${i}`}
                    onClick={() => setActive(i)}
                    className={`text-xs font-bold transition-colors duration-150 ${
                      active === i ? "text-indigo-300" : "text-zinc-300"
                    }`}
                  >
                    {s.material || quoteColor || "Unassigned"} {s.thickness}
                  </button>
                  {rateFor?.(s.material) > 0 && (
                    <span
                      data-testid={`layout-slab-rate-${i}`}
                      className="font-mono-data text-[10px] font-semibold text-emerald-300"
                    >
                      {money(rateFor(s.material))}/ft²
                    </span>
                  )}
                  {s.serial_number && (
                    <span
                      data-testid={`layout-slab-serial-${i}`}
                      className="font-mono-data rounded-full border border-yellow-500/30 bg-yellow-500/10 px-2 py-0.5 text-[10px] text-yellow-300"
                    >
                      {s.serial_number}
                    </span>
                  )}
                  {s.vein_angle != null && (
                    <span
                      data-testid={`layout-slab-veins-${i}`}
                      title={s.vein_notes || ""}
                      className="font-mono-data rounded-full border border-amber-400/30 bg-amber-500/10 px-2 py-0.5 text-[10px] text-amber-200"
                    >
                      veins {Math.round(s.vein_angle)}° {s.vein_movement ? `· ${s.vein_movement}` : ""}
                    </span>
                  )}
                  {s.bookmatch_of && (
                    <span
                      data-testid={`layout-slab-bookmatch-${i}`}
                      className="rounded-full border border-cyan-400/30 bg-cyan-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-cyan-200"
                    >
                      bookmatch twin
                    </span>
                  )}
                  <button
                    type="button"
                    data-testid={`layout-slab-delete-${i}`}
                    onClick={() => removeSlab(i)}
                    className="text-zinc-600 transition-colors duration-150 hover:text-red-400"
                    title="Remove this slab"
                  >
                    <Trash size={13} weight="bold" />
                  </button>
                  <button
                    type="button"
                    data-testid={`layout-slab-flip-${i}`}
                    onClick={() => toggleMirror(i)}
                    className={`transition-colors duration-150 ${
                      s.mirror
                        ? "text-cyan-300"
                        : "text-zinc-600 hover:text-cyan-300"
                    }`}
                    title="Flip this slab — mirror the veins for a bookmatch"
                  >
                    <FlipHorizontal size={13} weight="bold" />
                  </button>
                  {s.mirror && (
                    <span
                      data-testid={`layout-slab-mirror-badge-${i}`}
                      className="rounded-full border border-cyan-400/40 bg-cyan-500/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-cyan-200"
                    >
                      Mirrored
                    </span>
                  )}
                  <span className="font-mono-data ml-auto text-[10px] text-zinc-500">
                    {s.length_in}" x {s.width_in}" · used {st.used} ft² (
                    {st.pct}%)
                  </span>
                </div>
                <div
                  className={`relative mx-auto ${active === i ? "z-10" : ""}`}
                  data-layout-sheet
                  style={{ width: `${zoom * 100}%` }}
                  data-testid={`layout-sheet-${i}`}
                >
                  <svg
                    viewBox={`0 0 ${s.length_in} ${s.width_in}`}
                    preserveAspectRatio="xMidYMid meet"
                    style={{
                      aspectRatio: `${s.length_in} / ${s.width_in}`,
                      height: "auto",
                      overflow: "visible",
                    }}
                    className={`w-full border bg-zinc-100 ${
                      active === i
                        ? "border-indigo-400"
                        : "border-dashed border-zinc-700"
                    } ${tool === "cut" ? "cursor-crosshair" : ""}`}
                    onPointerMove={(e) => onMove(e, i)}
                    onPointerUp={() => {
                      setDrag(null);
                      setCutDrag(null);
                    }}
                    onPointerLeave={() => {
                      setDrag(null);
                      setCutDrag(null);
                      setCutHover(null);
                    }}
                  >
                    <rect
                      width={s.length_in}
                      height={s.width_in}
                      fill="#e7e5e4"
                    />
                    {s.photo_url && (
                      <>
                        <clipPath id={`slab-clip-${s.id || i}`}>
                          <rect width={s.length_in} height={s.width_in} />
                        </clipPath>
                        <g
                          transform={
                            s.mirror
                              ? `translate(${s.length_in},0) scale(-1,1)`
                              : undefined
                          }
                        >
                          <image
                            data-testid={`layout-slab-photo-${i}`}
                            href={photoHref(s.photo_url)}
                            clipPath={`url(#slab-clip-${s.id || i})`}
                            preserveAspectRatio="xMidYMid slice"
                            {...photoBox(s)}
                          />
                        </g>
                      </>
                    )}
                    {tool === "cut" && cutHover?.slabIndex === i && (() => {
                      const hit = on.find((p) => {
                        const pl = placements[p.id];
                        const geo = rotatePoints(p.points, pl.rot || 0);
                        return cutHover.x >= pl.x && cutHover.x <= pl.x + geo.w && cutHover.y >= pl.y && cutHover.y <= pl.y + geo.h;
                      });
                      if (!hit) return null;
                      const pl = placements[hit.id];
                      const geo = rotatePoints(hit.points, pl.rot || 0);
                      const v = cutDir === "v";
                      return (
                        <g data-testid="layout-cut-preview" className="pointer-events-none">
                          <line
                            x1={v ? cutHover.x : pl.x - 2}
                            y1={v ? pl.y - 2 : cutHover.y}
                            x2={v ? cutHover.x : pl.x + geo.w + 2}
                            y2={v ? pl.y + geo.h + 2 : cutHover.y}
                            stroke="#fafafa"
                            strokeWidth="0.5"
                            strokeDasharray="1.5 1"
                          />
                          <text
                            x={v ? cutHover.x + 1 : pl.x + geo.w + 3}
                            y={v ? pl.y - 3 : cutHover.y + 1}
                            fontSize="2.4"
                            fontWeight="700"
                            fill="#fafafa"
                          >
                            {inTag(v ? cutHover.x - pl.x : cutHover.y - pl.y)}
                          </text>
                        </g>
                      );
                    })()}
                    {on.map((p) => {
                      const pl = placements[p.id];
                      const geo = rotatePoints(p.points, pl.rot || 0);
                      const isSel = selected === p.id;
                      const off =
                        pl.x < -0.01 ||
                        pl.y < -0.01 ||
                        pl.x + geo.w > s.length_in + 0.01 ||
                        pl.y + geo.h > s.width_in + 0.01;
                      return (
                        <g
                          key={p.id}
                          data-testid={`layout-piece-${p.id}`}
                          data-off-slab={off ? "1" : "0"}
                          transform={`translate(${pl.x} ${pl.y})`}
                          onPointerDown={(e) => onPieceDown(e, p, i)}
                          onContextMenu={(e) => openMenu(e, p, i)}
                          onDoubleClick={(e) => {
                            e.stopPropagation();
                            rotate(p.id);
                          }}
                          className={
                            tool === "cut" ? "cursor-crosshair" : "cursor-grab"
                          }
                        >
                          <polygon
                            points={geo.pts
                              .map(([x, y]) => `${x},${y}`)
                              .join(" ")}
                            fill={
                              off
                                ? "rgba(245,158,11,0.32)"
                                : isSel
                                  ? "rgba(34,197,94,0.42)"
                                  : "rgba(34,197,94,0.26)"
                            }
                            stroke={off ? "#b45309" : isSel ? "#065f46" : "#15803d"}
                            strokeWidth="0.6"
                            strokeDasharray={off ? "2 1.5" : undefined}
                          />
                          {off && (
                            <text
                              x={geo.w / 2}
                              y={Math.min(geo.h - 1.5, 4)}
                              textAnchor="middle"
                              fontSize="2.6"
                              fontWeight="700"
                              fill="#fbbf24"
                              className="pointer-events-none select-none"
                              data-testid={`layout-piece-off-${p.id}`}
                            >
                              HANGS OFF SLAB · CUT A SEAM
                            </text>
                          )}
                          {geo.pts.map(([ax, ay], n) => {
                            const [bx, by] = geo.pts[(n + 1) % geo.pts.length];
                            const edge = (p.edges || [])[n] || {};
                            // backsplash / waterfall panels cut like any other piece
                            const e = p.panel_kind
                              ? { ...edge, seam: false }
                              : edge;
                            const len = Math.hypot(bx - ax, by - ay);
                            const vertical = Math.abs(bx - ax) < 0.5;
                            const mx = (ax + bx) / 2;
                            const my = (ay + by) / 2;
                            const pull =
                              Math.hypot(geo.w / 2 - mx, geo.h / 2 - my) || 1;
                            const off = dimSize(s) * 1.5;
                            const tx = mx + ((geo.w / 2 - mx) / pull) * off;
                            const ty = my + ((geo.h / 2 - my) / pull) * off;
                            return (
                              <g key={`e${n}`}>
                                <line
                                  x1={ax}
                                  y1={ay}
                                  x2={bx}
                                  y2={by}
                                  stroke={e.seam ? "#b45309" : "#14532d"}
                                  strokeWidth={e.seam ? 0.8 : 0.4}
                                  strokeDasharray={e.seam ? "2 1.5" : undefined}
                                />
                                {e.seam && seamNumbers[`${p.id}:${n}`] && (
                                  <g data-testid={`layout-seam-badge-${p.id}-${n}`} className="pointer-events-none">
                                    <rect x={mx - 2} y={my - 2} width={4} height={4} rx={0.5} fill="#fafafa" stroke="#111827" strokeWidth={0.3} />
                                    <text x={mx} y={my + 0.1} fontSize="2.8" fontWeight="800" fill="#111827" textAnchor="middle" dominantBaseline="middle">
                                      {seamNumbers[`${p.id}:${n}`]}
                                    </text>
                                  </g>
                                )}
                                {len > 16 && (
                                  <text
                                    x={tx}
                                    y={ty}
                                    fill="#052e16"
                                    stroke="rgba(255,255,255,0.85)"
                                    strokeWidth={dimSize(s) * 0.28}
                                    paintOrder="stroke"
                                    fontSize={dimSize(s)}
                                    fontWeight="bold"
                                    textAnchor="middle"
                                    dominantBaseline="middle"
                                    transform={
                                      vertical
                                        ? `rotate(-90 ${tx} ${ty})`
                                        : undefined
                                    }
                                    className="pointer-events-none select-none"
                                  >
                                    {`${inTag(len)}${e.seam ? " seam" : ""}`}
                                  </text>
                                )}
                              </g>
                            );
                          })}
                          {(p.cutouts || []).map((c) => (
                            <polygon
                              key={c.id}
                              data-testid={`layout-cutout-${c.id}`}
                              points={rotateCutout(c, p.points, pl.rot || 0)}
                              fill="rgba(248,250,252,0.75)"
                              stroke="#0f172a"
                              strokeWidth="0.35"
                              strokeDasharray="1.5 1"
                            />
                          ))}
                          <text
                            x={geo.w / 2}
                            y={geo.h / 2}
                            fill="#052e16"
                            stroke="rgba(255,255,255,0.85)"
                            strokeWidth={dimSize(s) * 0.3}
                            paintOrder="stroke"
                            fontSize={dimSize(s) * 1.15}
                            fontWeight="bold"
                            textAnchor="middle"
                            dominantBaseline="middle"
                            className="pointer-events-none select-none"
                          >
                            {`${inTag(geo.w)} x ${inTag(geo.h)} · ${p.sqft} ft²`}
                          </text>
                          {isSel && (
                            <g
                              data-testid={`layout-rotate-handle-${p.id}`}
                              className="cursor-pointer"
                              onPointerDown={(e) => {
                                e.stopPropagation();
                                rotate(p.id);
                              }}
                            >
                              <circle
                                cx={geo.w - 3.5}
                                cy={3.5}
                                r={dimSize(s) * 1.5}
                                fill="#065f46"
                                stroke="#a7f3d0"
                                strokeWidth="0.3"
                              />
                              <path
                                d={`M ${geo.w - 3.5 - 1.4} ${3.5 + 0.4} a 1.5 1.5 0 1 1 1.5 1.2`}
                                fill="none"
                                stroke="#ecfdf5"
                                strokeWidth="0.45"
                              />
                              <path
                                d={`M ${geo.w - 3.5 + 0.1} ${3.5 + 2.1} l 0.9 -0.5 l -0.1 1.1 z`}
                                fill="#ecfdf5"
                              />
                            </g>
                          )}
                        </g>
                      );
                    })}
                    {cuts
                      .filter((c) => c.slab === i)
                      .map((c) => {
                        const v = c.dir === "v";
                        const x1 = v ? c.at : 0;
                        const y1 = v ? 0 : c.at;
                        const x2 = v ? c.at : s.length_in;
                        const y2 = v ? s.width_in : c.at;
                        const on = selCut === c.id;
                        const grab = (e) => {
                          e.stopPropagation();
                          setSelCut(c.id);
                          setActive(i);
                          setCutDrag({ id: c.id, slab: i });
                        };
                        return (
                          <g key={c.id} data-testid={`layout-cut-${c.id}`}>
                            <line
                              x1={x1}
                              y1={y1}
                              x2={x2}
                              y2={y2}
                              stroke={on ? "#fbbf24" : "#f59e0b"}
                              strokeWidth={on ? 1 : 0.5}
                              strokeDasharray="3 2"
                            />
                            <line
                              data-testid={`layout-cut-hit-${c.id}`}
                              x1={x1}
                              y1={y1}
                              x2={x2}
                              y2={y2}
                              stroke="transparent"
                              strokeWidth={Math.max(2.5, s.length_in / 50)}
                              className="cursor-move"
                              onPointerDown={grab}
                            />
                            <circle
                              data-testid={`layout-cut-handle-${c.id}`}
                              cx={v ? c.at : s.length_in / 2}
                              cy={v ? s.width_in / 2 : c.at}
                              r={Math.max(1.2, s.length_in / 90)}
                              fill="#f59e0b"
                              stroke="#09090b"
                              strokeWidth="0.3"
                              className="cursor-grab"
                              onPointerDown={grab}
                            />
                          </g>
                        );
                      })}
                  </svg>
                  {on.map((p) => {
                    const pl = placements[p.id];
                    return (
                      <span
                        key={p.id}
                        data-testid={`layout-label-${p.id}`}
                        style={{
                          left: `${(pl.x / s.length_in) * 100}%`,
                          top: `${(pl.y / s.width_in) * 100}%`,
                        }}
                        className="pointer-events-none absolute ml-1 mt-1 rounded bg-emerald-950/85 px-1.5 py-0.5 text-[10px] font-bold text-emerald-50"
                      >
                        {p.area_name} · {p.label}
                      </span>
                    );
                  })}
                  {cuts
                    .filter((c) => c.slab === i && selCut === c.id)
                    .map((c) => (
                      <div
                        key={c.id}
                        data-testid={`layout-cut-panel-${c.id}`}
                        style={{
                          left:
                            c.dir === "v"
                              ? `${(c.at / s.length_in) * 100}%`
                              : "50%",
                          top:
                            c.dir === "v"
                              ? "50%"
                              : `${(c.at / s.width_in) * 100}%`,
                        }}
                        className="absolute z-20 flex -translate-x-1/2 -translate-y-1/2 items-center gap-1 rounded-md border border-amber-500/50 bg-zinc-950/95 p-1"
                      >
                        <span className="px-1 text-[10px] font-bold uppercase tracking-widest text-amber-300">
                          Seam
                        </span>
                        <input
                          type="number"
                          step="0.5"
                          data-testid={`layout-cut-at-${c.id}`}
                          value={c.at}
                          onChange={(ev) =>
                            patch({
                              cuts: cuts.map((x) =>
                                x.id === c.id
                                  ? { ...x, at: Number(ev.target.value) }
                                  : x,
                              ),
                            })
                          }
                          className="font-mono-data h-8 w-20 rounded border border-zinc-700 bg-zinc-900 px-2 text-xs text-zinc-100 outline-none focus:border-amber-400"
                        />
                        <button
                          type="button"
                          data-testid={`layout-cut-delete-${c.id}`}
                          title="Remove this seam"
                          onClick={() => {
                            patch({ cuts: cuts.filter((x) => x.id !== c.id) });
                            setSelCut(null);
                          }}
                          className="rounded border border-red-500/40 bg-red-500/10 p-1.5 text-red-300 transition-colors duration-150 hover:bg-red-500/20"
                        >
                          <Trash size={13} weight="bold" />
                        </button>
                      </div>
                    ))}
                  {menu &&
                    menu.slabIndex === i &&
                    (() => {
                      const mp = pieces.find((p) => p.id === menu.pieceId);
                      if (!mp) return null;
                      return (
                        <div
                          data-testid="layout-piece-menu"
                          style={{ left: menu.left, top: menu.top }}
                          className="absolute z-30 flex max-w-[94%] -translate-x-1/2 flex-wrap items-center justify-center gap-1.5 rounded-xl border border-zinc-700 bg-zinc-900/95 p-1.5 shadow-2xl backdrop-blur"
                          onPointerDown={(e) => e.stopPropagation()}
                          onContextMenu={(e) => e.preventDefault()}
                        >
                          <div className="rounded-lg bg-zinc-800 px-2.5 py-1 text-center">
                            <span className="block text-[8px] font-bold uppercase tracking-widest text-zinc-500">
                              Area
                            </span>
                            <span
                              className="font-mono-data block text-[11px] font-bold text-zinc-100"
                              data-testid="menu-sqft"
                            >
                              {(polyArea(mp.points) / 144).toFixed(2)} SF
                            </span>
                          </div>
                          <button
                            type="button"
                            data-testid="menu-rotate-left"
                            title="Rotate anti-clockwise"
                            onClick={() => rotate(menu.pieceId, -menuAngle)}
                            className="rounded-lg border border-zinc-700 p-2 text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
                          >
                            <ArrowCounterClockwise size={14} weight="bold" />
                          </button>
                          <Select
                            value={String(menuAngle)}
                            onValueChange={(v) => setMenuAngle(Number(v))}
                          >
                            <SelectTrigger
                              data-testid="menu-angle"
                              className="h-9 w-24 border-zinc-700 bg-zinc-950 text-xs"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {[1, 5, 15, 22.5, 45, 90].map((a) => (
                                <SelectItem key={a} value={String(a)}>
                                  {a}°
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <button
                            type="button"
                            data-testid="menu-rotate"
                            title="Rotate clockwise"
                            onClick={() => rotate(menu.pieceId, menuAngle)}
                            className="rounded-lg border border-zinc-700 p-2 text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
                          >
                            <ArrowClockwise size={14} weight="bold" />
                          </button>
                          {slabs.length > 1 && (
                            <Select
                              value={String(menu.slabIndex)}
                              onValueChange={(v) =>
                                moveToSlab(menu.pieceId, Number(v))
                              }
                            >
                              <SelectTrigger
                                data-testid="menu-slab"
                                className="h-9 w-36 border-zinc-700 bg-zinc-950 text-xs"
                              >
                                <SelectValue placeholder="Slab" />
                              </SelectTrigger>
                              <SelectContent>
                                {slabs.map((sl, si) => (
                                  <SelectItem
                                    key={sl.id || si}
                                    value={String(si)}
                                  >
                                    {`Slab ${si + 1} · ${sl.material || quoteColor || "Unassigned"}`}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                          <button
                            type="button"
                            data-testid="menu-seam-v"
                            title="Add a seam down the piece"
                            onClick={() => seamFromMenu("v")}
                            className="flex items-center gap-1 rounded-lg border border-amber-500/40 bg-amber-500/10 p-2 text-amber-200 transition-colors duration-150 hover:bg-amber-500/20"
                          >
                            <Scissors size={14} weight="bold" />
                            <span className="text-[10px] font-bold">↕</span>
                          </button>
                          <button
                            type="button"
                            data-testid="menu-seam-h"
                            title="Add a seam across the piece"
                            onClick={() => seamFromMenu("h")}
                            className="flex items-center gap-1 rounded-lg border border-amber-500/40 bg-amber-500/10 p-2 text-amber-200 transition-colors duration-150 hover:bg-amber-500/20"
                          >
                            <Scissors size={14} weight="bold" />
                            <span className="text-[10px] font-bold">↔</span>
                          </button>
                          {(() => {
                            const p = pieces.find((x) => x.id === menu.pieceId);
                            const pl = p && placements[p.id];
                            const s = slabs[menu.slabIndex];
                            if (!p || !pl || !s) return null;
                            const geo = rotatePoints(p.points, pl.rot || 0);
                            const edge =
                              pl.x < -0.01
                                ? { dir: "v", at: 0 }
                                : pl.x + geo.w > s.length_in + 0.01
                                  ? { dir: "v", at: s.length_in }
                                  : pl.y < -0.01
                                    ? { dir: "h", at: 0 }
                                    : pl.y + geo.h > s.width_in + 0.01
                                      ? { dir: "h", at: s.width_in }
                                      : null;
                            if (!edge) return null;
                            return (
                              <button
                                type="button"
                                data-testid="menu-seam-slab-edge"
                                title="Cut the seam exactly where the piece crosses the slab edge"
                                onClick={() => {
                                  const { srcAxis, at } = sourceCut(
                                    p.points,
                                    pl.rot || 0,
                                    (edge.dir === "v" ? edge.at : menu.at.x) - pl.x,
                                    (edge.dir === "h" ? edge.at : menu.at.y) - pl.y,
                                    edge.dir === "v" ? 0 : 1,
                                  );
                                  onSplitPiece?.(p.area_id, p.id, srcAxis, at);
                                  setMenu(null);
                                }}
                                className="flex items-center gap-1.5 rounded-lg border border-amber-400 bg-amber-500/25 px-2.5 py-2 text-[10px] font-bold uppercase tracking-widest text-amber-100 transition-colors duration-150 hover:bg-amber-500/40"
                              >
                                <Scissors size={14} weight="bold" /> Seam at slab edge
                              </button>
                            );
                          })()}
                          {onMergeSeam && pieces.find((p) => p.id === menu.pieceId)?.edges?.some((e) => e.seam) && (
                            <button
                              type="button"
                              data-testid="menu-remove-seam"
                              title="Remove the seam — weld this piece back onto its other half"
                              onClick={() => {
                                const p = pieces.find((x) => x.id === menu.pieceId);
                                onMergeSeam(p.area_id, p.id);
                                setMenu(null);
                              }}
                              className="flex items-center gap-1 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-2 py-2 text-[10px] font-bold uppercase tracking-widest text-emerald-200 transition-colors duration-150 hover:bg-emerald-500/20"
                            >
                              <Scissors size={14} weight="bold" className="rotate-90" /> Remove seam
                            </button>
                          )}
                          <button
                            type="button"
                            data-testid="menu-remove"
                            title="Remove from the slab"
                            onClick={() => {
                              unplace(menu.pieceId);
                              setMenu(null);
                            }}
                            className="rounded-lg border border-zinc-700 p-2 text-red-400 transition-colors duration-150 hover:bg-red-500/10"
                          >
                            <Trash size={14} weight="bold" />
                          </button>
                        </div>
                      );
                    })()}
                </div>
              </div>
            );
          })}
        </div>

        {onPortalShown && (
          <label
            data-testid="layout-portal-toggle"
            className="mt-3 flex cursor-pointer items-center gap-2.5 border-t border-zinc-800 px-3 py-3 text-xs text-zinc-300"
          >
            <input
              type="checkbox"
              data-testid="layout-portal-checkbox"
              checked={portalShown}
              onChange={(e) => onPortalShown(e.target.checked)}
              className="h-4 w-4 accent-indigo-500"
            />
            Show this slab layout in the customer portal
          </label>
        )}
      </div>

      <aside
        className="shrink-0 border-t border-zinc-800 bg-zinc-900/60 p-3 xl:w-64 xl:border-l xl:border-t-0"
        data-testid="layout-slab-detail"
      >
        <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
          Slab detail
        </div>
        {(() => {
          const rows = [];
          const seen = new Set();
          pieces.forEach((p) =>
            (p.edges || []).forEach((e, k) => {
              const num = seamNumbers[`${p.id}:${k}`];
              if (!num || seen.has(num)) return;
              seen.add(num);
              const pl = placements[p.id];
              const geo = pl ? rotatePoints(p.points, pl.rot || 0) : null;
              const a = p.points[k], b = p.points[(k + 1) % p.points.length];
              rows.push({ num, p, k, len: Math.hypot(b[0] - a[0], b[1] - a[1]), slab: pl?.slab, geo, pl });
            }),
          );
          rows.sort((x, y) => x.num - y.num);
          return (
            <div className="mt-3 rounded-lg border border-zinc-800 bg-zinc-950/60 p-2" data-testid="layout-seam-list">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
                  Seams · {rows.length}
                </span>
              </div>
              {!rows.length ? (
                <p className="mt-1 text-[11px] text-zinc-500">No seams yet — use the scissors to cut one.</p>
              ) : (
                <ul className="mt-1.5 space-y-1">
                  {rows.map((r) => (
                    <li
                      key={r.num}
                      data-testid={`layout-seam-row-${r.num}`}
                      className={`flex items-center gap-2 rounded-md px-1.5 py-1 text-[11px] ${
                        selected === r.p.id ? "bg-amber-500/10 text-amber-100" : "text-zinc-300"
                      }`}
                    >
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-zinc-100 text-[10px] font-extrabold text-zinc-900">
                        {r.num}
                      </span>
                      <button
                        type="button"
                        data-testid={`layout-seam-jump-${r.num}`}
                        title="Jump to this seam"
                        onClick={() => {
                          if (r.pl && r.pl.slab != null) setActive(r.pl.slab);
                          setSelected(r.p.id);
                          const sheet = sheetsRef.current?.querySelector(`[data-testid="layout-sheet-${r.pl?.slab ?? 0}"]`);
                          sheet?.scrollIntoView({ behavior: "smooth", block: "nearest" });
                        }}
                        className="min-w-0 flex-1 truncate text-left hover:text-white"
                      >
                        <span className="font-semibold">{r.p.label}</span>
                        <span className="font-mono-data text-zinc-500"> · {inTag(r.len)}</span>
                        {r.pl?.slab != null && <span className="text-zinc-500"> · slab {r.pl.slab + 1}</span>}
                      </button>
                      {onMergeSeam && (
                        <button
                          type="button"
                          data-testid={`layout-seam-remove-${r.num}`}
                          title="Remove this seam — weld the halves back together"
                          onClick={() => onMergeSeam(r.p.area_id, r.p.id)}
                          className="shrink-0 rounded p-1 text-zinc-500 transition-colors duration-150 hover:bg-red-500/15 hover:text-red-300"
                        >
                          <X size={12} weight="bold" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })()}
        {!activeSlab ? (
          <p className="mt-3 text-[11px] text-zinc-500">
            Pick a slab to change its material, size or see its waste.
          </p>
        ) : (
          <div className="mt-3 space-y-3">
            <div className="space-y-1">
              <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
                Material
              </span>
              <SearchSelect
                testId="layout-detail-material"
                value={activeSlab.material || ""}
                options={materialOptions}
                allowCustom
                placeholder="Assign material"
                searchPlaceholder="Slabs, remnants or a color…"
                onChange={(v) => {
                  const [kind, color] = String(v).split("|");
                  setSlab(active, {
                    material:
                      kind === "slab" ||
                      kind === "remnant" ||
                      kind === "catalog"
                        ? color
                        : v,
                  });
                }}
                className="h-10 px-3 text-xs"
              />
            </div>
            <div className="space-y-1">
              <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
                Slab size (W x H)
              </span>
              <div className="flex items-center gap-2">
                <Input
                  data-testid="layout-detail-length"
                  type="number"
                  value={activeSlab.length_in}
                  onChange={(e) =>
                    setSlab(active, { length_in: Number(e.target.value) || 0 })
                  }
                  className="h-10 border-zinc-800 bg-zinc-950 text-right font-mono-data text-xs"
                />
                <span className="text-xs text-zinc-500">x</span>
                <Input
                  data-testid="layout-detail-width"
                  type="number"
                  value={activeSlab.width_in}
                  onChange={(e) =>
                    setSlab(active, { width_in: Number(e.target.value) || 0 })
                  }
                  className="h-10 border-zinc-800 bg-zinc-950 text-right font-mono-data text-xs"
                />
              </div>
            </div>
            <div className="space-y-1" data-testid="layout-photo-tools">
              <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
                Slab photo
              </span>
              <div className="flex gap-2">
                <Button
                  data-testid="layout-upload-photo"
                  variant="outline"
                  disabled={uploading}
                  onClick={() => photoRef.current?.click()}
                  className="h-10 flex-1 border-zinc-700 bg-transparent text-[10px] uppercase tracking-widest hover:bg-zinc-800"
                >
                  <UploadSimple size={13} weight="bold" className="mr-1" />
                  {uploading
                    ? "Uploading…"
                    : activeSlab.photo_url
                      ? "Replace"
                      : "Upload"}
                </Button>
                {activeSlab.photo_url && (
                  <Button
                    data-testid="layout-remove-photo"
                    variant="outline"
                    onClick={() =>
                      setSlab(active, {
                        photo_url: "",
                        photo_zoom: 1,
                        photo_x: 0,
                        photo_y: 0,
                      })
                    }
                    className="h-10 border-zinc-700 bg-transparent text-[10px] uppercase tracking-widest text-red-400 hover:bg-zinc-800"
                  >
                    <Trash size={13} weight="bold" />
                  </Button>
                )}
              </div>
              <input
                ref={photoRef}
                data-testid="layout-photo-input"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={uploadPhoto}
              />
              <button
                type="button"
                data-testid="layout-photo-from-inventory"
                onClick={() => setInvPick(true)}
                className="flex h-9 w-full items-center justify-center gap-1.5 rounded-md border border-zinc-700 text-[10px] font-bold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
              >
                <ImageSquare size={13} weight="bold" />
                From inventory or remnant
              </button>
              <InventoryPhotoDialog
                open={invPick}
                onClose={() => setInvPick(false)}
                inventorySlabs={inventorySlabs}
                material={activeSlab.material || ""}
                onPick={(it, resize) => {
                  setSlab(active, {
                    photo_url: it.path,
                    photo_zoom: 1,
                    photo_x: 0,
                    photo_y: 0,
                    ...(resize && it.length_in && it.width_in
                      ? { length_in: it.length_in, width_in: it.width_in }
                      : {}),
                  });
                  setInvPick(false);
                  toast.success(
                    `${it.kind === "remnant" ? "Remnant" : "Slab"} photo from ${it.color || "inventory"} applied${
                      resize && it.length_in && it.width_in ? ` · sheet ${it.length_in}" × ${it.width_in}"` : ""
                    }`,
                  );
                }}
              />
              {activeSlab.photo_url && (
                <div className="space-y-2 rounded-lg border border-zinc-800 bg-zinc-950 p-2.5">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500">
                    <Crop size={12} weight="bold" className="text-indigo-300" />
                    Crop to {activeSlab.length_in}" x {activeSlab.width_in}"
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-10 text-[10px] uppercase tracking-widest text-zinc-500">
                      Zoom
                    </span>
                    <input
                      data-testid="layout-photo-zoom"
                      type="range"
                      min="0.5"
                      max="3"
                      step="0.05"
                      value={Number(activeSlab.photo_zoom) || 1}
                      onChange={(e) =>
                        setSlab(active, {
                          photo_zoom: Number(e.target.value) || 1,
                        })
                      }
                      className="h-1.5 flex-1 accent-indigo-400"
                    />
                    <span className="font-mono-data w-9 text-right text-[10px] text-zinc-400">
                      {Math.round((Number(activeSlab.photo_zoom) || 1) * 100)}%
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-10 text-[10px] uppercase tracking-widest text-zinc-500">
                      Shift
                    </span>
                    <Input
                      data-testid="layout-photo-x"
                      type="number"
                      step="any"
                      value={Number(activeSlab.photo_x) || 0}
                      onChange={(e) =>
                        setSlab(active, {
                          photo_x: Number(e.target.value) || 0,
                        })
                      }
                      className="font-mono-data h-9 border-zinc-800 bg-zinc-900 text-right text-xs"
                    />
                    <Input
                      data-testid="layout-photo-y"
                      type="number"
                      step="any"
                      value={Number(activeSlab.photo_y) || 0}
                      onChange={(e) =>
                        setSlab(active, {
                          photo_y: Number(e.target.value) || 0,
                        })
                      }
                      className="font-mono-data h-9 border-zinc-800 bg-zinc-900 text-right text-xs"
                    />
                  </div>
                  <Button
                    data-testid="layout-photo-fit"
                    variant="outline"
                    onClick={() =>
                      setSlab(active, { photo_zoom: 1, photo_x: 0, photo_y: 0 })
                    }
                    className="h-9 w-full border-zinc-700 bg-transparent text-[10px] uppercase tracking-widest hover:bg-zinc-800"
                  >
                    Fit to slab
                  </Button>
                  <p className="text-[10px] leading-relaxed text-zinc-500">
                    Anything outside the slab is cropped off, so pieces sit over
                    the real veining.
                  </p>
                </div>
              )}
            </div>
            {(() => {
              const st = slabStats(active);
              return (
                <div
                  className="rounded-lg border border-zinc-800 bg-zinc-950 p-2.5"
                  data-testid="layout-detail-stats"
                >
                  <div className="h-1.5 overflow-hidden rounded-full bg-zinc-800">
                    <div
                      className="h-full bg-indigo-400 transition-all duration-300"
                      style={{ width: `${Math.min(st.pct, 100)}%` }}
                    />
                  </div>
                  <div className="font-mono-data mt-2 flex justify-between text-[10px]">
                    <span className="text-zinc-400">
                      Used {st.used} / {st.total} ft² ({st.pct}%)
                    </span>
                    <span className="text-amber-300">
                      Waste {Math.round((st.total - st.used) * 100) / 100} ft²
                    </span>
                  </div>
                  <div className="font-mono-data mt-1 text-[10px] text-zinc-500">
                    {st.count} piece{st.count === 1 ? "" : "s"} on this slab
                  </div>
                </div>
              );
            })()}
            {selected && placements[selected] && (
              <div className="flex gap-2">
                <Button
                  data-testid="layout-rotate-piece"
                  variant="outline"
                  onClick={() => rotate(selected)}
                  className="h-10 flex-1 border-zinc-700 bg-transparent text-[10px] uppercase tracking-widest hover:bg-zinc-800"
                >
                  <ArrowsClockwise size={13} weight="bold" className="mr-1" />
                  Rotate
                </Button>
                <Button
                  data-testid="layout-unplace-piece"
                  variant="outline"
                  onClick={() => unplace(selected)}
                  className="h-10 border-zinc-700 bg-transparent text-[10px] uppercase tracking-widest text-amber-300 hover:bg-zinc-800"
                >
                  Take off
                </Button>
              </div>
            )}
          </div>
        )}
      </aside>
    </div>
  );
};

export default SlabLayoutBoard;
